/**
 * <s-product-gallery> — PDP main image carousel with thumbnails.
 *
 * Required markup:
 *   [data-gallery-track]          — horizontal scroll-snap container of slides.
 *   [data-gallery-slide]          — each slide inside the track.
 *   [data-gallery-thumb][data-index] — thumbnail buttons; clicking scrolls to that slide.
 *   [data-gallery-prev] / [data-gallery-next] — optional arrow buttons.
 * The active thumbnail receives aria-current="true".
 */
class SProductGallery extends HTMLElement {
  #ac;
  #observer;
  #index = 0;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    this.track = this.querySelector('[data-gallery-track]');
    if (!this.track) return;
    this.slides = [...this.track.querySelectorAll('[data-gallery-slide]')];
    this.thumbs = [...this.querySelectorAll('[data-gallery-thumb]')];

    this.addEventListener('click', this.#handleClick, opts);

    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) this.#setActive(this.slides.indexOf(entry.target));
        }
      },
      { root: this.track, threshold: 0.6 },
    );
    this.slides.forEach((slide) => this.#observer.observe(slide));
    this.#setActive(0);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#observer?.disconnect();
  }

  #handleClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const thumb = event.target.closest('[data-gallery-thumb]');
    if (thumb) {
      this.goTo(Number(thumb.dataset.index));
      return;
    }
    if (event.target.closest('[data-gallery-prev]')) this.goTo(this.#index - 1);
    if (event.target.closest('[data-gallery-next]')) this.goTo(this.#index + 1);
  };

  goTo(index) {
    const count = this.slides.length;
    if (!count) return;
    const next = (index + count) % count;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.track.scrollTo({ left: this.slides[next].offsetLeft, behavior: reduce ? 'auto' : 'smooth' });
    this.#setActive(next);
  }

  #setActive(index) {
    if (index < 0) return;
    this.#index = index;
    this.thumbs.forEach((thumb, i) => {
      if (i === index) {
        thumb.setAttribute('aria-current', 'true');
        // Scroll only the thumbnail rail (never the page) to keep the active thumb visible.
        const rail = thumb.closest('[data-gallery-thumbs]');
        if (rail) {
          rail.scrollTo({
            top: thumb.offsetTop - rail.clientHeight / 2 + thumb.offsetHeight / 2,
            left: thumb.offsetLeft - rail.clientWidth / 2 + thumb.offsetWidth / 2,
          });
        }
      } else {
        thumb.removeAttribute('aria-current');
      }
    });
  }
}

if (!customElements.get('s-product-gallery')) {
  customElements.define('s-product-gallery', SProductGallery);
}
